import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  /** Optional fallback render fn. Defaults to a simple panel. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors so a single bad component can't blank the
 * entire UI. Production should report `error` and `info` to a telemetry
 * sink (Sentry, etc.) — left as a TODO; for now we just log.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Replace with your telemetry of choice.
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      const requestId =
        error instanceof ApiError ? error.requestId : null;
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive" />
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground max-w-md">
              The UI hit an unexpected error. Try reloading the page or
              return to the dashboard.
            </p>
          </div>
          <pre className="max-w-xl overflow-auto rounded-md border bg-muted p-3 text-left text-xs text-muted-foreground">
            {error.message}
          </pre>
          {requestId ? (
            <p className="text-xs text-muted-foreground">
              Request ID:{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">
                {requestId}
              </code>
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button variant="outline" onClick={this.reset}>
              <RotateCcw className="h-4 w-4" />
              Try again
            </Button>
            <Button onClick={() => window.location.assign("/")}>
              Back to dashboard
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
